import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Phone, FileText, ArrowRight, MessageCircle, Mail, Share2, Facebook, Link2 } from "lucide-react";
import Layout, { useChatContext } from "@/components/layout/Layout";
import cindyAsset from "@/assets/chat-cindy-banner.webp.asset.json";
import LeadForm from "@/components/LeadForm";
import ImageZoomViewer from "@/components/ui/ImageZoomViewer";
import ManufacturerFeatureNotice, { MANUFACTURER_FEATURE_HEADING } from "@/components/inventory/ManufacturerFeatureNotice";
import { inventoryUnitQueryOptions } from "@/hooks/useInventory";
import { Button } from "@/components/ui/button";

// Test-only brochure layout (noindex, unlinked). Shows only the unit's saved data.
export const Route = createFileRoute("/brochure-test/$slug")({
  loader: async ({ params, context }) => {
    const unit = await context.queryClient.ensureQueryData(inventoryUnitQueryOptions(params.slug));
    if (!unit) throw notFound();
    return { title: `${unit.year ?? ""} ${unit.make ?? ""} ${unit.model ?? ""} ${unit.trim ?? ""}`.replace(/\s+/g, " ").trim() };
  },
  head: ({ loaderData, params }) => {
    const t = loaderData?.title || "RV Brochure";
    const url = `https://www.rexandsonsrvs.com/brochure-test/${params.slug}`;
    return {
      meta: [
        { title: `${t} Digital Brochure | Rex & Sons RVs` },
        { name: "description", content: `Browse the ${t} brochure from Rex & Sons RVs in Castle Hayne, NC.` },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:type", content: "product" },
        { property: "og:title", content: `${t} Digital Brochure` },
        { property: "og:description", content: `Photos, floorplan and specs for the ${t}.` },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  component: BrochurePage,
});


// General accessory suggestions linking to parts search (not claims about this unit's equipment).
const ADD_ONS = [
  { name: "Surge Protector / EMS", search: "surge protector", blurb: "Protects your RV's electrical system from voltage spikes and faulty park power." },
  { name: "Leveling Blocks", search: "leveling blocks", blurb: "Get a stable, level setup on uneven campsites." },
  { name: "Water Pressure Regulator", search: "water pressure regulator", blurb: "Guards your plumbing against high campground water pressure." },
  { name: "Sewer Hose Kit", search: "sewer hose", blurb: "A clean, reliable dump-station setup from day one." },
  { name: "Wheel Chocks", search: "wheel chock", blurb: "Keeps your RV secure and steady while parked." },
  { name: "Outdoor Camping Rug", search: "camping rug", blurb: "Extends your living space under the awning." },
];

function BrochurePage() {
  const { slug } = Route.useParams();
  const { data: unit } = useSuspenseQuery(inventoryUnitQueryOptions(slug));
  const [zoom, setZoom] = useState<number | null>(null);
  if (!unit) return null;

  const title = `${unit.year ?? ""} ${unit.make ?? ""} ${unit.model ?? ""} ${unit.trim ?? ""}`.replace(/\s+/g, " ").trim();
  const photos = (unit.images ?? []).filter(Boolean);

  const specs: [string, string | number | null][] = [
    ["Length", unit.length_ft ? `${unit.length_ft} ft` : null],
    ["Sleeps", unit.sleeps],
    ["Slides", unit.slides],
    ["Chassis", unit.chassis],
    ["Engine", unit.engine],
    ["Fuel", unit.fuel_type],
    ["Weight", unit.weight_lbs ? `${unit.weight_lbs.toLocaleString()} lbs` : null],
    ["Fresh water", unit.fresh_water_gal ? `${unit.fresh_water_gal} gal` : null],
    ["Grey water", unit.grey_water_gal ? `${unit.grey_water_gal} gal` : null],
    ["Black water", unit.black_water_gal ? `${unit.black_water_gal} gal` : null],
    ["Refrigerator", unit.refrigerator],
    ["Water heater", unit.water_heater],
    ["Exterior", unit.exterior_color],
    ["Interior", unit.interior_color],
  ];
  const shownSpecs = specs.filter(([, v]) => v !== null && v !== "" && v !== undefined);
  const story = photos.slice(1, 7);
  const gallery = photos.slice(7);
  // Split the unit's own saved description into an overview + photo captions (no invented copy).
  const sentences = (unit.description ?? "").replace(/\s+/g, " ").match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g)?.map((s) => s.trim()).filter(Boolean) ?? [];
  const overview = sentences.slice(0, 2).join(" ");
  const rest = sentences.slice(2);
  const per = Math.max(1, Math.ceil(rest.length / Math.max(1, story.length)));
  const captions = story.map((_, i) => rest.slice(i * per, i * per + per).join(" "));

  return (
    <Layout>
      <article className="bg-background text-foreground">
        {/* Hero */}
        <header className="relative h-[55vh] min-h-[360px] md:h-[70vh] md:min-h-[420px] w-full overflow-hidden bg-secondary">
          {photos[0] && <img src={photos[0]} alt={title} className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />}
          <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/40 to-transparent" />
          <div className="relative mx-auto flex h-full max-w-6xl flex-col justify-end px-6 pb-12 text-secondary-foreground">
            <p className="font-heading text-sm uppercase tracking-[0.3em] text-accent">{unit.condition} · Stock #{unit.stock_number}</p>
            <h1 className="mt-2 font-heading text-4xl font-bold uppercase leading-tight md:text-6xl">{title}</h1>
          </div>
        </header>

        {/* Cindy welcome */}
        <div className="mx-auto max-w-6xl px-6 pt-10">
          <CindyWelcome model={unit.model} />
        </div>

        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-14 lg:grid-cols-[1fr_340px]">
          <div className="space-y-16">
            {overview && (
              <section>
                <h2 className="font-heading text-3xl font-bold uppercase">The Overview</h2>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{overview}</p>
              </section>
            )}

            {story.length > 0 && (
              <section className="space-y-10">
                {story.map((src, i) => (
                  <figure key={src} className="space-y-4">
                    <button type="button" onClick={() => setZoom(i + 1)} className="block w-full overflow-hidden rounded-lg">
                      <img src={src} alt={`${title} photo ${i + 2}`} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-500 hover:scale-[1.02]" />
                    </button>
                    {captions[i] && <figcaption className="text-lg leading-relaxed text-muted-foreground">{captions[i]}</figcaption>}
                  </figure>
                ))}
              </section>
            )}

            <section>
              <h2 className="font-heading text-3xl font-bold uppercase">Popular Add-Ons</h2>
              <p className="mt-3 text-muted-foreground">Accessories RV owners often pick up — shop them in our parts store.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {ADD_ONS.map((a) => (
                  <a key={a.name} href={`/parts-catalog/results?search=${encodeURIComponent(a.search)}`} className="group block border-l-4 border-primary bg-card p-4 transition-colors hover:bg-muted">
                    <p className="font-heading text-lg">{a.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{a.blurb}</p>
                    <span className="mt-2 inline-flex items-center text-sm font-semibold text-primary">Shop parts <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </a>
                ))}
              </div>
            </section>

            {unit.floorplan_image && (
              <section>
                <h2 className="font-heading text-3xl font-bold uppercase">Floorplan</h2>
                <img src={unit.floorplan_image} alt={`${title} floorplan`} loading="lazy" className="mt-4 w-full rounded-lg border border-border bg-card p-4" />
              </section>
            )}

            {shownSpecs.length > 0 && (
              <section>
                <h2 className="font-heading text-3xl font-bold uppercase">By the Numbers</h2>
                <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
                  {shownSpecs.map(([k, v]) => (
                    <div key={k} className="bg-card p-4">
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{k}</dt>
                      <dd className="mt-1 font-heading text-lg">{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {(unit.features?.length ?? 0) > 0 && (
              <section>
                <h2 className="font-heading text-3xl font-bold uppercase">{MANUFACTURER_FEATURE_HEADING}</h2>
                <div className="mt-4"><ManufacturerFeatureNotice /></div>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  {unit.features.map((f) => (
                    <li key={f} className="border-l-2 border-primary pl-3 text-sm">{f}</li>
                  ))}
                </ul>
              </section>
            )}

            {gallery.length > 0 && (
              <section>
                <h2 className="font-heading text-3xl font-bold uppercase">More Photos</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                  {gallery.map((src, i) => (
                    <button key={src} type="button" onClick={() => setZoom(i + 7)} className="overflow-hidden rounded-md">
                      <img src={src} alt={`${title} photo ${i + 8}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section id="brochure-lead" className="rounded-lg border border-border bg-card p-6">
              <h2 className="font-heading text-2xl font-bold uppercase">Check Availability</h2>
              <LeadForm sourceDisplay="Digital Brochure" vehicleYear={unit.year ?? ""} vehicleMake={unit.make ?? ""} vehicleModel={unit.model ?? ""} stockNumber={unit.stock_number} showVehicleSection={false} />
            </section>
          </div>

          {/* Sticky CTA panel */}
          <BrochureSidePanel title={title} stickerUrl={unit.window_sticker_url} listingSlug={unit.slug} />
        </div>
      </article>
      <ImageZoomViewer photos={photos.map((src) => ({ src }))} index={zoom} onIndexChange={setZoom} title={title} />
    </Layout>
  );
}

function CindyWelcome({ model }: { model: string | null | undefined }) {
  const { setChat } = useChatContext();
  return (
          <button type="button" onClick={() => setChat((p) => ({ ...p, isOpen: true }))} className="flex w-full items-center gap-5 rounded-2xl border border-border bg-card p-4 text-left shadow-md transition-shadow hover:shadow-lg md:p-5">
            <img src={cindyAsset.url} alt="Cindy from Rex & Sons" className="h-20 w-20 shrink-0 rounded-full object-cover object-[5%_20%] ring-4 ring-primary/20 md:h-24 md:w-24" />
            <div className="min-w-0 flex-1">
              <p className="font-heading text-xl uppercase md:text-2xl">Hi, I'm Cindy 👋</p>
              <p className="mt-1 text-muted-foreground">Picturing yourself in this {model ?? "RV"}? Ask me about payments, trade-ins, towing or a walkaround video — I'll get you answers fast.</p>
            </div>
            <span className="hidden shrink-0 rounded-full bg-primary px-5 py-2.5 font-heading text-sm uppercase text-primary-foreground sm:inline-flex">Ask Cindy</span>
          </button>
  );
}

function BrochureSidePanel({ title, stickerUrl, listingSlug }: { title: string; stickerUrl?: string | null; listingSlug: string }) {
  const { setChat } = useChatContext();
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const enc = encodeURIComponent(url);
  const text = encodeURIComponent(title);
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* dismissed */ }
    } else copy();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const btn = "flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm transition-colors hover:bg-muted";
  return (
    <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
      <div className="space-y-3 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <button type="button" onClick={() => setChat((p) => ({ ...p, isOpen: true }))} className={`${btn} py-3.5 text-base font-medium`}>
          <MessageCircle className="h-5 w-5" /> Let's Talk
        </button>
        <div className="grid grid-cols-2 gap-3">
          <a href="tel:9107932894" className={btn}><Phone className="h-4 w-4" /> Call</a>
          <a href="#brochure-lead" className={btn}><Mail className="h-4 w-4" /> Email</a>
        </div>
        {stickerUrl && (
          <a href={stickerUrl} target="_blank" rel="noopener noreferrer" className={btn}><FileText className="h-4 w-4" /> Window Sticker</a>
        )}
        <Link to="/rv-search/$slug" params={{ slug: listingSlug }} className="flex items-center justify-center gap-2 pt-1 text-sm text-muted-foreground hover:text-foreground">
          View pricing &amp; full listing <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-3 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <p className="font-heading text-xl uppercase">Share This RV</p>
        <button type="button" onClick={share} className={btn}><Share2 className="h-4 w-4" /> Share</button>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`} target="_blank" rel="noopener noreferrer" className={btn}><Facebook className="h-4 w-4" /> Facebook</a>
        <a href={`https://twitter.com/intent/tweet?url=${enc}&text=${text}`} target="_blank" rel="noopener noreferrer" className={btn}>X (Twitter)</a>
        <a href={`mailto:?subject=${text}&body=${enc}`} className={btn}><Mail className="h-4 w-4" /> Email</a>
        <button type="button" onClick={copy} className={btn}><Link2 className="h-4 w-4" /> {copied ? "Copied!" : "Copy Link"}</button>
      </div>
    </aside>
  );
}
